import React, { useCallback } from "react";
import {
  ClinicianPortalApiError,
  clinicianPortalApi,
} from "../api/clinicianPortalApi";
import { ActivityTypePill, RecordDetailCard, RecordFieldRow } from "../components/DataDisplay";
import { ActivityStepsEditor } from "../components/ActivityStepsEditor";
import { ErrorState, LoadingState } from "../components/FeedbackStates";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import {
  activityTemplateDetailPath,
  homeworkActivityDetailPath,
} from "../config/navigation";
import {
  buildHomeworkEditStepsUrl,
  buildTemplateEditStepsUrl,
} from "../config/legacyEditor";
import { usePortalData } from "../hooks/usePortalData";
import { withClinicianCode } from "../routing/portalRoutes";
import { formatPortalDate } from "../utils/dateTime";

type ActivityStepsEditorSource =
  | { kind: "template"; templateId: string }
  | {
    kind: "homework";
    patientId: string;
    homeworkAssignmentId: string;
  };

type ActivityStepsPageData =
  | Awaited<
    ReturnType<typeof clinicianPortalApi.getActivityTemplate>
  >
  | Awaited<
    ReturnType<typeof clinicianPortalApi.getHomeworkDetail>
  >;

function safeReturnPath(fallback: string): string {
  const raw = new URLSearchParams(window.location.search).get("returnUrl");
  if (!raw) return fallback;
  try {
    const url = new URL(raw, window.location.origin);
    if (
      url.origin !== window.location.origin ||
      !url.pathname.startsWith("/clinician-portal")
    ) {
      return fallback;
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return fallback;
  }
}

export function ActivityStepsEditorPage({
  clinicianCode,
  source,
}: {
  clinicianCode: string;
  source: ActivityStepsEditorSource;
}) {
  const request = useCallback(
    (signal: AbortSignal) =>
      source.kind === "template"
        ? clinicianPortalApi.getActivityTemplate(
          clinicianCode,
          source.templateId,
          signal,
        )
        : clinicianPortalApi.getHomeworkDetail(
          clinicianCode,
          source.patientId,
          source.homeworkAssignmentId,
          signal,
        ),
    [clinicianCode, source],
  );
  const state = usePortalData<ActivityStepsPageData>(request);
  const data =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;

  const templateData = source.kind === "template" && data?.operation === "portal.getActivityTemplate" ? data : undefined;
  const homeworkData = source.kind === "homework" && data?.operation === "portal.getHomeworkDetail" ? data : undefined;
  const template = templateData?.template ?? null;
  const homework = homeworkData?.homework ?? null;
  const patient = homeworkData?.patient ?? null;
  const therapist = data?.therapist ?? null;
  const available = source.kind === "template"
    ? Boolean(templateData?.success && templateData.found && templateData.authorized && templateData.therapist && template)
    : Boolean(homeworkData?.success && homeworkData.found && homeworkData.authorized && homeworkData.therapist && patient && homework);

  const fallbackDetailPath = source.kind === "template"
    ? activityTemplateDetailPath(source.templateId)
    : homeworkActivityDetailPath(source.patientId, source.homeworkAssignmentId);
  const returnPath = withClinicianCode(
    safeReturnPath(fallbackDetailPath),
    clinicianCode,
  );
  const activityName = template?.name || homework?.homeworkName || "Activity Steps";
  const activityType = template?.activityType || homework?.activityType || "";
  const initialValue = template?.activitySteps ?? homework?.activitySteps ?? "";
  const fallbackUrl = source.kind === "template"
    ? buildTemplateEditStepsUrl({
      clinicianCode,
      templateId: source.templateId,
      returnUrl: new URL(returnPath, window.location.origin).toString(),
    })
    : buildHomeworkEditStepsUrl({
      clinicianCode,
      patientId: source.patientId,
      homeworkAssignmentId: source.homeworkAssignmentId,
      returnUrl: new URL(returnPath, window.location.origin).toString(),
    });

  const saveSteps = async (activitySteps: string) => {
    if (source.kind === "template") {
      const result = await clinicianPortalApi.updateActivityTemplateSteps(
        clinicianCode,
        source.templateId,
        activitySteps,
      );
      if (
        !result.success ||
        !result.found ||
        !result.authorized ||
        result.template?.id !== source.templateId ||
        result.template.activitySteps !== activitySteps
      ) {
        throw new ClinicianPortalApiError(
          "The Activity Template steps could not be verified after saving.",
          "INVALID_RESPONSE",
          false,
        );
      }
      return;
    }

    const result = await clinicianPortalApi.updateHomeworkSteps(
      clinicianCode,
      source.patientId,
      source.homeworkAssignmentId,
      { activitySteps },
    );
    if (
      !result.success ||
      !result.found ||
      !result.authorized ||
      result.homework?.id !== source.homeworkAssignmentId ||
      result.homework.activitySteps !== activitySteps
    ) {
      throw new ClinicianPortalApiError(
        "The Homework Activity steps could not be verified after saving.",
        "INVALID_RESPONSE",
        false,
      );
    }
  };

  return (
    <ClinicianPortalLayout
      activeGlobalKey={source.kind === "template" ? "templates" : "homework"}
      clinicianCode={clinicianCode}
      therapistRole={therapist?.role}
    >
      <PortalHeader
        title={activityName}
        clinicianName={therapist?.therapistName}
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={[
              {
                label: source.kind === "template" ? "Activity Templates" : "Homework Activities",
                path: source.kind === "template" ? "/clinician-portal/activity-templates" : "/clinician-portal/homework-activities",
              },
              { label: activityName, path: fallbackDetailPath },
              { label: "Edit Steps" },
            ]}
          />
        }
      />
      <div className="cp-page-content cp-page-content--profile">
        {state.status === "loading" ? <LoadingState label="Loading Activity Steps" /> : null}
        {state.status === "error" ? <ErrorState error={state.error} onRetry={state.refresh} /> : null}
        {data && !available ? (
          <ErrorState
            error={
              new ClinicianPortalApiError(
                "This Activity Steps record is unavailable.",
                data.authorized ? "NOT_FOUND" : "NOT_AUTHORIZED",
                false,
              )
            }
            onRetry={state.refresh}
          />
        ) : null}
        {available ? (
          <>
            <RecordDetailCard>
              <RecordFieldRow label="Activity Type">
                <ActivityTypePill activityType={activityType} />
              </RecordFieldRow>
              {source.kind === "homework" ? (
                <>
                  <RecordFieldRow label="Client">
                    {patient?.accessCode} • {patient?.nickname}
                  </RecordFieldRow>
                  <RecordFieldRow label="Due Date">
                    {formatPortalDate(homework?.dueDate)}
                  </RecordFieldRow>
                </>
              ) : null}
            </RecordDetailCard>
            <ActivityStepsEditor
              key={`${source.kind}-${source.kind === "template" ? source.templateId : source.homeworkAssignmentId}`}
              initialValue={initialValue}
              sourceKind={source.kind}
              returnPath={returnPath}
              fallbackUrl={fallbackUrl}
              onSave={saveSteps}
            />
          </>
        ) : null}
      </div>
    </ClinicianPortalLayout>
  );
}
